// Portal-export classification: contract + structured property type.
export const CONTRACT_TYPES = ["vendita", "affitto"] as const;
export type ContractType = (typeof CONTRACT_TYPES)[number];
export const CONTRACT_TYPE_LABEL: Record<ContractType, string> = { vendita: "Vendita", affitto: "Affitto" };

export const PROPERTY_TYPES = [
  "appartamento", "attico", "villa", "villetta_a_schiera", "bifamiliare",
  "rustico_casale", "terreno", "nuova_costruzione", "mansarda", "loft",
  "locale_commerciale", "ufficio", "box_garage",
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const PROPERTY_TYPE_LABEL: Record<PropertyType, string> = {
  appartamento: "Appartamento",
  attico: "Attico",
  villa: "Villa",
  villetta_a_schiera: "Villetta a schiera",
  bifamiliare: "Bifamiliare",
  rustico_casale: "Rustico / Casale",
  terreno: "Terreno",
  nuova_costruzione: "Nuova costruzione",
  mansarda: "Mansarda",
  loft: "Loft",
  locale_commerciale: "Locale commerciale",
  ufficio: "Ufficio",
  box_garage: "Box / Garage",
};

// Immobiliare.it transaction code.
export const immobiliareTransaction = (c: ContractType): "S" | "R" => (c === "affitto" ? "R" : "S");

// NOTE: the exact Immobiliare.it building/@IDType codes come from their XSD and
// are confirmed at account activation. Left empty on purpose — the feed emits the
// human label + our enum; the Immobiliare-specific adapter fills IDType later.
export const IMMOBILIARE_IDTYPE: Partial<Record<PropertyType, number>> = {};

// ============================================================================
// Portal syndication — one source -> per-portal feeds + readiness.
// Two feed FORMATS: "it" (Immobiliare.it-style XML, used by the Italian
// pull-based portals) and "kyero" (Kyero v3, the public international standard
// accepted by foreign-buyer portals). The exact per-portal credentials, IP
// whitelist and XSD mapping remain the agency's at account activation — these
// feeds are valid, hand-them-to-the-portal XML, not a fake real-time push.
// ============================================================================

export const FEED_FORMATS = ["it", "kyero"] as const;
export type FeedFormat = (typeof FEED_FORMATS)[number];

export type PortalProfile = {
  key: string;
  name: string;
  format: FeedFormat;
  /** readiness keys this portal needs for a valid listing (see fieldPresent) */
  requiredFields: string[];
  /** foreign-buyer focused (DACH / international) */
  foreign: boolean;
  note: string;
};

export const PORTALS: readonly PortalProfile[] = [
  { key: "immobiliare", name: "Immobiliare.it", format: "it", foreign: false, note: "IT · #1", requiredFields: ["title", "property_type", "contract", "prezzo", "mq", "ape", "city", "images"] },
  { key: "idealista", name: "Idealista.it", format: "it", foreign: false, note: "IT · #2 · multilingua", requiredFields: ["title", "property_type", "contract", "prezzo", "mq", "city", "images"] },
  { key: "casa", name: "Casa.it", format: "it", foreign: false, note: "IT · #3", requiredFields: ["title", "property_type", "contract", "prezzo", "mq", "city", "images"] },
  { key: "wikicasa", name: "Wikicasa.it", format: "it", foreign: false, note: "IT · agenzie", requiredFields: ["title", "property_type", "contract", "prezzo", "city", "images"] },
  { key: "trovacasa", name: "Trovacasa.it", format: "it", foreign: false, note: "IT · aggregatore", requiredFields: ["title", "property_type", "contract", "prezzo", "city"] },
  { key: "subito", name: "Subito.it", format: "it", foreign: false, note: "IT · generalista", requiredFields: ["title", "contract", "prezzo", "city"] },
  { key: "gate_away", name: "Gate-away.com", format: "kyero", foreign: true, note: "Estero · DACH/intl", requiredFields: ["title", "property_type", "contract", "prezzo", "description", "images", "province"] },
  { key: "green_acres", name: "Green-Acres", format: "kyero", foreign: true, note: "Estero · case in IT per stranieri", requiredFields: ["title", "property_type", "contract", "prezzo", "description", "images"] },
];

export const PORTAL_KEYS = PORTALS.map((p) => p.key);
export const portalByKey = (key: string): PortalProfile | undefined => PORTALS.find((p) => p.key === key);

/** IT province sigla -> human name (international feeds want a readable province). */
export const PROVINCE_NAME: Record<string, string> = {
  VR: "Verona", BS: "Brescia", MN: "Mantova", TN: "Trento", BZ: "Bolzano",
  MI: "Milano", MB: "Monza e Brianza", BG: "Bergamo", CO: "Como", LC: "Lecco", VA: "Varese",
  VE: "Venezia", PD: "Padova", VI: "Vicenza", TV: "Treviso", RO: "Rovigo", BL: "Belluno",
  BO: "Bologna", MO: "Modena", RM: "Roma", TO: "Torino", FI: "Firenze", NA: "Napoli",
};
export const provinceLabel = (p: string | null | undefined): string =>
  p ? (PROVINCE_NAME[p.toUpperCase()] ?? p) : "";

/** av/estate property_type -> Kyero v3 <type> vocabulary. */
export const KYERO_TYPE: Record<PropertyType, string> = {
  appartamento: "apartment", attico: "penthouse", villa: "villa", villetta_a_schiera: "townhouse",
  bifamiliare: "semi-detached", rustico_casale: "country-house", terreno: "plot",
  nuova_costruzione: "new-development", mansarda: "loft", loft: "loft",
  locale_commerciale: "commercial", ufficio: "commercial", box_garage: "garage",
};

/** Flattened view of a published listing, sufficient to judge portal readiness. */
export type ListingReadiness = {
  status: string;
  prezzo: number | null;
  mq: number | null;
  ape: string | null;
  property_type: string | null;
  contract: string | null;
  city: string | null;
  province: string | null;
  titleIt: string | null;
  descriptionIt: string | null;
  imageCount: number;
};

export const READINESS_FIELD_LABEL: Record<string, string> = {
  title: "Titolo", description: "Descrizione", images: "Foto", prezzo: "Prezzo",
  mq: "Superficie", ape: "Classe energetica", property_type: "Tipologia",
  contract: "Contratto", city: "Città", province: "Provincia",
};

function fieldPresent(key: string, r: ListingReadiness): boolean {
  switch (key) {
    case "title": return !!r.titleIt;
    case "description": return !!r.descriptionIt;
    case "images": return r.imageCount > 0;
    case "prezzo": return r.prezzo != null || r.status === "prezzo_su_richiesta";
    case "mq": return r.mq != null;
    case "ape": return !!r.ape;
    case "property_type": return !!r.property_type;
    case "contract": return !!r.contract;
    case "city": return !!r.city;
    case "province": return !!r.province;
    default: return true;
  }
}

/** Human-labelled list of required fields a listing is MISSING for a portal. */
export function missingPortalFields(profile: PortalProfile, r: ListingReadiness): string[] {
  return profile.requiredFields
    .filter((k) => !fieldPresent(k, r))
    .map((k) => READINESS_FIELD_LABEL[k] ?? k);
}
